import { TextArea } from "@intility/bifrost-react";
import Button from "@intility/bifrost-react/Button";
import Input from "@intility/bifrost-react/Input";
import Message from "@intility/bifrost-react/Message";
import { useState } from "react";
import { useOnboardCompany } from "~/api/companies";
import type { OnboardedCompany } from "~/api/types";
import { buildOnboardBody, parseList } from "./onboarding";
import { ConsentCopyButton } from "./TenantList";

// One form for every onboarding call (ADR-0058): a new company, completion of an imported row,
// more tenants or domains, and a retry. Without a company it onboards a new one.
export function OnboardForm({
  company,
  onDone,
}: {
  company?: OnboardedCompany;
  onDone: () => void;
}) {
  const [companyId, setCompanyId] = useState(company?.companyId ?? "");
  const [orgSlug, setOrgSlug] = useState(company?.orgSlug ?? "");
  const [displayName, setDisplayName] = useState(company?.displayName ?? "");
  const [tenantIds, setTenantIds] = useState("");
  const [domains, setDomains] = useState("");
  const onboard = useOnboardCompany();

  const newTenants = parseList(tenantIds);

  const handleSubmit = () => {
    const body = buildOnboardBody(company, {
      orgSlug,
      displayName,
      tenantIds,
      domains,
    });
    onboard.mutate(
      { companyId: companyId.trim(), body },
      { onSuccess: onDone },
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <ReadinessNotice />

      <Input
        label="Company ID (GUID)"
        description="The company GUID from Company Service. It cannot change after onboarding."
        placeholder="020a40ac-5674-4027-adc5-0134335ead1c"
        value={companyId}
        onChange={(e) => setCompanyId(e.target.value)}
        disabled={company !== undefined}
        required
      />
      <Input
        label="Organization slug"
        description="Lowercase letters, digits and hyphens. It cannot change after it is set. If the access store already links the company to an organization, use that slug."
        placeholder="acme"
        value={orgSlug}
        onChange={(e) => setOrgSlug(e.target.value)}
        disabled={Boolean(company?.orgSlug)}
        required
      />
      <Input
        label="Display name"
        description="The name of the Keycloak Organization."
        placeholder="Acme AS"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        required
      />

      {company && company.tenants.length > 0 && (
        <ExistingList
          label="Tenants"
          items={company.tenants.map((t) => t.tenantId)}
        />
      )}
      <TextArea
        label={company ? "Tenants to add" : "Entra tenant IDs"}
        description="One or more tenant GUIDs. Separate them with commas or new lines."
        rows={2}
        value={tenantIds}
        onChange={(e) => setTenantIds(e.target.value)}
        required={!company || company.tenants.length === 0}
      />
      {newTenants.length > 0 && (
        <div className="bf-small">
          Admin consent:{" "}
          {newTenants.map((id, i) => (
            <span key={id}>
              {i > 0 && ", "}
              <ConsentCopyButton tenantId={id} />
            </span>
          ))}
        </div>
      )}

      {company && company.domains.length > 0 && (
        <ExistingList label="Domains" items={company.domains} />
      )}
      <TextArea
        label={company ? "Domains to add" : "Email domains"}
        description="A domain belongs to exactly one company. Separate them with commas or new lines."
        rows={2}
        placeholder="acme.no"
        value={domains}
        onChange={(e) => setDomains(e.target.value)}
        required={!company || company.domains.length === 0}
      />

      {company?.error && !onboard.error && (
        <Message
          state="alert"
          header={`The last run failed at ${company.failedStep ?? "an unknown step"}`}
        >
          {company.error}
        </Message>
      )}
      {onboard.error && (
        <Message state="alert" header="Onboarding failed">
          {onboard.error instanceof Error
            ? onboard.error.message
            : String(onboard.error)}
        </Message>
      )}

      <div>
        <Button
          variant="filled"
          onClick={handleSubmit}
          state={
            onboard.isPending || !companyId.trim() ? "inactive" : undefined
          }
        >
          {onboard.isPending ? "Onboarding…" : "Onboard"}
        </Button>
      </div>
    </div>
  );
}

// ADR-0058 decision 10: the page shows what the worker needs, it does not check it.
export function ReadinessNotice() {
  return (
    <Message state="warning" header="Before you onboard">
      Make sure of two things in each tenant of the company: the Intility ID app
      registration has admin consent, and its API permissions are granted.
      Onboarding does not check this. Without them, the user sync cannot read
      users from the tenant. Copy the consent link of each tenant.
    </Message>
  );
}

function ExistingList({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="bf-small">
      <span style={{ color: "var(--bfc-base-c-2)" }}>{label} now: </span>
      {items.map((item, i) => (
        <span key={item}>
          {i > 0 && ", "}
          <code>{item}</code>
        </span>
      ))}
    </div>
  );
}
